// P6ix AI 대시보드
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Sparkles, Brain, FileText, Image, TrendingUp,
  Zap, MessageSquare, CheckCircle, Clock, BarChart3
} from "lucide-react";
import PageHeader from "../../components/common/ui/PageHeader";

export default function AIDashboard() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalChats: 0,
    documentsAnalyzed: 0,
    imagesProcessed: 0,
    predictions: 0,
  });

  // 통계 로딩 (실제로는 API 호출)
  useEffect(() => {
    // TODO: API 연동
    setStats({
      totalChats: 247,
      documentsAnalyzed: 89,
      imagesProcessed: 156,
      predictions: 34,
    });
  }, [projectId]);

  const featureCards = [
    {
      title: "AI 챗봇",
      description: "법규, 문서, 공정 통합 질문",
      icon: MessageSquare,
      color: "from-blue-500 to-blue-600",
      path: "/ai/chat",
      badge: "인기",
    },
    {
      title: "문서 자동 요약",
      description: "계약서/보고서 AI 요약",
      icon: FileText,
      color: "from-purple-500 to-purple-600",
      path: "/ai/document/summary",
      badge: "신규",
    },
    {
      title: "안전 이미지 분석",
      description: "시공사진 위험 요소 자동 탐지",
      icon: Image,
      color: "from-orange-500 to-red-600",
      path: "/ai/vision/safety",
      badge: "추천",
    },
    {
      title: "공정 지연 예측",
      description: "AI 기반 공정 리스크 분석",
      icon: TrendingUp,
      color: "from-green-500 to-emerald-600",
      path: "/ai/predict/schedule",
    },
    {
      title: "자재 수요 예측",
      description: "향후 자재 소요량 AI 예측",
      icon: BarChart3,
      color: "from-indigo-500 to-indigo-600",
      path: "/ai/predict/material",
    },
    {
      title: "위험성 평가 AI",
      description: "작업별 위험성 자동 평가",
      icon: CheckCircle,
      color: "from-red-500 to-pink-600",
      path: "/ai/automation/risk",
      badge: "신규",
    },
  ];

  const recentActivities = [
    { type: "chat", content: "안전관리 법규 질문", time: "5분 전" },
    { type: "document", content: "도급계약서 요약 완료", time: "1시간 전" },
    { type: "vision", content: "시공사진 12장 분석 완료", time: "2시간 전" },
    { type: "prediction", content: "자재 수요 예측 보고서 생성", time: "3시간 전" },
  ];

  const handleCardClick = (path) => {
    navigate(`/project/${projectId}${path}`);
  };

  return (
    <div className="p-6">
      <PageHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <span>P6ix AI 종합 인사이트</span>
        </div>
      </PageHeader>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-700">총 대화</span>
            <MessageSquare className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-3xl font-bold text-blue-900">{stats.totalChats}</div>
          <p className="text-xs text-blue-600 mt-1">+12% 지난주 대비</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-purple-700">문서 분석</span>
            <FileText className="w-5 h-5 text-purple-600" />
          </div>
          <div className="text-3xl font-bold text-purple-900">{stats.documentsAnalyzed}</div>
          <p className="text-xs text-purple-600 mt-1">+8% 지난주 대비</p>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-orange-700">이미지 분석</span>
            <Image className="w-5 h-5 text-orange-600" />
          </div>
          <div className="text-3xl font-bold text-orange-900">{stats.imagesProcessed}</div>
          <p className="text-xs text-orange-600 mt-1">+25% 지난주 대비</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-700">예측 분석</span>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-green-900">{stats.predictions}</div>
          <p className="text-xs text-green-600 mt-1">+18% 지난주 대비</p>
        </div>
      </div>

      {/* 주요 기능 카드 */}
      <div className="mb-8">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Brain className="w-5 h-5" />
          주요 AI 기능
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {featureCards.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <button
                key={idx}
                onClick={() => handleCardClick(feature.path)}
                className="group bg-white border border-gray-200 rounded-lg p-6 hover:shadow-xl transition-all duration-200 text-left relative overflow-hidden"
              >
                {/* 배지 */}
                {feature.badge && (
                  <span className="absolute top-3 right-3 px-2 py-1 text-xs font-bold bg-purple-100 text-purple-700 rounded-full">
                    {feature.badge}
                  </span>
                )}

                {/* 아이콘 */}
                <div className={`inline-flex p-3 bg-gradient-to-br ${feature.color} rounded-lg mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>

                {/* 제목 */}
                <h3 className="font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                  {feature.title}
                </h3>

                {/* 설명 */}
                <p className="text-sm text-gray-600">
                  {feature.description}
                </p>

                {/* 호버 효과 */}
                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.color} transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left`}></div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 최근 활동 */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          최근 AI 활동
        </h2>
        <div className="space-y-3">
          {recentActivities.map((activity, idx) => {
            const icons = {
              chat: MessageSquare,
              document: FileText,
              vision: Image,
              prediction: TrendingUp,
            };
            const ActivityIcon = icons[activity.type];

            return (
              <div key={idx} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <ActivityIcon className="w-4 h-4 text-gray-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.content}</p>
                  <p className="text-xs text-gray-500">{activity.time}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 안내 메시지 */}
      <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Zap className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-bold text-purple-900 mb-1">💡 AI 활용 팁</h3>
            <p className="text-sm text-purple-700">
              P6ix AI는 OpenAI GPT-4o를 기반으로 건설 현장에 특화된 인사이트를 제공합니다.
              왼쪽 메뉴에서 원하는 AI 기능을 선택하거나, 챗봇에 자유롭게 질문해보세요.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
